import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import {
  GitFork, MessageSquare, FileText, Scale, GitBranch, Activity,
  ShieldQuestion, CheckCircle2, Sparkles,
} from "lucide-react";
import { useStore } from "@/store/useStore";
import { Avatar, EmptyState } from "@/components/AppShell";
import { Badge } from "@/components/Badge";
import { StrainCard } from "@/components/StrainCard";

export default function Profile() {
  const { username } = useParams<{ username: string }>();
  const strains = useStore((s) => s.strains);
  const users = useStore((s) => s.users);
  const currentUser = useStore((s) => s.user);
  const loadUsers = useStore((s) => s.loadUsers);
  const user = (username === currentUser?.username ? currentUser : users.find((u) => u.username === username)) ?? currentUser ?? users[0];

  useEffect(() => {
    if (!users.length) loadUsers().catch(() => undefined);
  }, [users.length, loadUsers]);

  if (!user) {
    return <EmptyState icon={<Sparkles size={24} />} title="User not found" desc="No profile is loaded for this user." />;
  }

  const createdStrains = strains.filter((s) => s.creator === user.username);
  const forkedStrains = createdStrains.filter((s) => s.slug.includes("fork"));

  const rep = user.reputation;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* header */}
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 mb-6">
        <div className="flex items-start gap-4">
          <Avatar initials={user.username.slice(0, 2).toUpperCase()} size={64} />
          <div className="flex-1">
            <h1 className="text-xl font-semibold">{user.display_name ?? user.username}</h1>
            <div className="text-[13px] text-[var(--color-ink-muted)] font-mono">@{user.username}</div>
            {user.bio && <p className="text-[13px] text-[var(--color-ink-soft)] mt-2 max-w-md">{user.bio}</p>}
          </div>
          <Badge tone="muted">{user.role}</Badge>
        </div>
      </div>

      {/* reputation signals — not one-dimensional karma */}
      {rep && (
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">reputation signals</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <RepSignal icon={<ShieldQuestion size={14} />} value={rep.helpful_objections} label="helpful objections" tone="amber" />
            <RepSignal icon={<CheckCircle2 size={14} />} value={rep.accepted_rewordings} label="accepted rewordings" tone="primary" />
            <RepSignal icon={<FileText size={14} />} value={rep.evidence_verified} label="evidence verified" tone="info" />
            <RepSignal icon={<Activity size={14} />} value={rep.minds_moved} label="minds moved" tone="inferred" />
            <RepSignal icon={<Scale size={14} />} value={rep.cross_camp_fair} label="cross-camp fair" tone="primary" />
            <RepSignal icon={<GitBranch size={14} />} value={rep.constructive_forks} label="constructive forks" tone="inferred" />
            <RepSignal icon={<Sparkles size={14} />} value={rep.cruxes_identified} label="cruxes found" tone="amber" />
            <RepSignal icon={<GitFork size={14} />} value={rep.bridges_accepted} label="bridges accepted" tone="primary" />
          </div>
        </div>
      )}

      {/* created strains */}
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">
          strains by {user.username} ({createdStrains.length})
        </div>
        {createdStrains.length === 0 ? (
          <EmptyState icon={<Sparkles size={24} />} title="No strains yet" desc="Create one to see it here." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {createdStrains.map((s, i) => <StrainCard key={s.id} strain={s} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function RepSignal({ icon, value, label, tone }: { icon: React.ReactNode; value: number; label: string; tone: string }) {
  const color = tone === "primary" ? "var(--color-primary)"
    : tone === "inferred" ? "var(--color-inferred)"
    : tone === "amber" ? "var(--color-secondary)"
    : "var(--color-define)";
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <div style={{ color }}>{icon}</div>
      <div className="text-xl font-semibold font-mono mt-1 text-[var(--color-ink)]">{value}</div>
      <div className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">{label}</div>
    </div>
  );
}
